/**
 * **The move forecast**: what committing a declared move would provoke, read before anything
 * commits (`projects/off-turn-planning.md` 1.3). The browser's drag callout and the CLI's
 * `forecast-move` both read this, so the two can never disagree.
 *
 * Reactions come from {@link compileMoveCheckpointOffers}, the sorted list `gatherMoveCheckpoint`
 * consults on the room's `request_move` path, so the forecast names exactly the offers the room
 * would halt on, in the order it would halt on them. Engagement comes from {@link isEngagedWith}
 * over a scratch state with the mover standing at `to`: not a reaction, but a move consequence the
 * player plans around (RAW p.61).
 *
 * Accuracy is "as the board stands now": watchers' reactions are read off the current round. The
 * caller picks the state; off-turn that is `planningTurnState`, since {@link movePathAnchors} and
 * the checkpoint gate both need the mover to be acting.
 *
 * **Only situational consequences** (`decisions/0186` item 3, the move advisory): every entry is
 * something THIS destination causes that another destination might not. Nothing true of every move
 * is ever an entry: not "moving closes the Protocol window", not "moving closes Ordnance", not
 * "moving costs movement". The moves left after the drop is a number on the result, not an entry.
 */
import {
  TRAMPLING_STATUS,
  applyEncounter,
  applyResolved,
  grapplePartner,
  charactersCrossed,
  hasStatus,
  hasSweptTrampleThisTurn,
  moveBoostFold,
  movePathAnchors,
  movementContext,
  newEngagersOnStep,
  worldFor,
  type BoostBlocker,
  type Cell,
  type EncounterState,
} from "@lancer-vtt/domain";
import { gatherStatusHandlers, SEED_OVERLAY, type LoadedOverlay } from "./effects";
import {
  AUTHORED_MOVE_REACTION_ID,
  OVERWATCH_CHECKPOINT_ID,
  compileMoveCheckpointOffers,
  moveCheckpointConsumerId,
  type MoveCheckpointContext,
  type MoveCheckpointOffer,
} from "./checkpointRegistry";
import { scriptedDiceSource } from "../dice";
import { isEngagedWith } from "./spatialRecompute";
import { dragRecomputer } from "./dragRecompute";
import { combatantRange } from "./effects/spatialAnswerer";
import { couldReachNextTurn } from "./nextTurnReach";

/**
 * One thing committing the move would cause, in this order: `boost`, `trample`, reactions (in the
 * room's halt order), `stops`, `drags` or `ends_grapple`, `engaged` (by id), `reachable_by`. A unit named by `stops` is not repeated as
 * `engaged`: the stop is the stronger fact about the same adjacency. A `boost_refused` entry is the
 * whole forecast of a drop the room refuses ({@link isRefusedForecast}).
 */
export type MoveForecastEntry =
  /** The destination is beyond the move left and inside a Boost's reach: the drop Boosts, then moves (`move-auto-boost`). `free` when the Boost costs no action. */
  | { kind: "boost"; free: boolean }
  /** Only a Boost reaches the destination and the `boost` reducer refuses it, for `blocker`'s reason: the drop is refused. */
  | { kind: "boost_refused"; blocker: BoostBlocker["kind"] }
  /** A Trampler's chosen path crosses these characters, and this is the turn's first crossing: each takes the Trample damage (`landing-zone-full-automation` Q3, Q4). */
  | { kind: "trample"; unitIds: string[]; unitNames: string[] }
  | {
      kind: "reaction";
      /** The checkpoint consumer that raises it: `overwatch` or `authored_move_reaction`. */
      consumerId: string;
      /** The deciding unit: the Overwatch watcher, or the authored mark's caster. */
      unitId: string;
      unitName: string;
      /** `Overwatch`, or the authored clause's reaction name (e.g. `covering_fire`). */
      reactionName: string;
    }
  /** The mover drags its grapple partner along (it is the dominant party, Core p.69). */
  | { kind: "drags"; unitId: string; unitName: string }
  /** The move breaks adjacency with the mover's grapple partner, which ends the grapple (Core p.69). */
  | { kind: "ends_grapple"; unitId: string; unitName: string }
  | { kind: "engaged"; unitId: string; unitName: string }
  /** The move stops here: `unitId` is a newly-Engaging hostile of the mover's Size or larger (Core p.62), so the unit's move drops to 0. */
  | { kind: "stops"; unitId: string; unitName: string }
  /** Hostiles that could attack the mover here on their next turn ({@link couldReachNextTurn}). */
  | { kind: "reachable_by"; unitIds: string[]; unitNames: string[] };

/** A legal destination's forecast: its entries and the move budget the mover holds after the drop (0 when the move stops). */
export type MoveForecast = { entries: MoveForecastEntry[]; moveLeftAfter: number };

/**
 * Forecast `moverId` moving to `to` against `state`. Pure, no dice. `null` when the destination is
 * not a legal move even after a Boost ({@link movePathAnchors} is `null` on both states): an illegal
 * drop has nothing to forecast. A destination only a Boost reaches is forecast against the state
 * the real `boost` reducer produces, led by a `boost` entry: the room folds that Boost into the
 * drop (`moveBoostFold`, the same decision `refereeMove` applies). When the reducer refuses the
 * Boost, the forecast is a lone `boost_refused` entry naming why. `overlay` defaults to
 * {@link SEED_OVERLAY}, as the room's own gather does.
 *
 * A move that carries its chosen `path` (Q1, a dragged Trampler's trail) is forecast along that
 * path: `null` when the path is illegal or over the move left even after a Boost, and a `trample`
 * entry naming who it crosses.
 */
export function forecastMove(
  state: EncounterState,
  move: ForecastedMove,
  overlay?: LoadedOverlay,
): MoveForecast | null {
  const standing = state.combatants[move.moverId];
  if (standing?.pos == null) return null;
  const fold = moveBoostFold(state, { moverId: move.moverId, to: move.to, actorId: move.moverId, ...(move.path !== undefined ? { path: move.path } : {}) });
  if (fold.kind === "refused") return { entries: [{ kind: "boost_refused", blocker: fold.blocker.kind }], moveLeftAfter: 0 };
  if (fold.kind === "boost") state = fold.state;
  const mover = state.combatants[move.moverId]!;
  const path = move.path !== undefined ? chosenPath(state, { moverId: move.moverId, to: move.to, path: move.path }) : movePathAnchors(state, mover, move.to);
  if (path === null || mover.pos == null) return null;

  // The same `declaredMove` shape `refereeMove`'s gather builds (`from` the mover's pre-move
  // anchor, `path` the route and `spaces` its steps, Q6), typed off the context so a new field
  // there breaks this build.
  const declaredMove: MoveCheckpointContext["declaredMove"] = { moverId: move.moverId, from: mover.pos, to: move.to, path, spaces: path.length - 1 };
  const ctx: MoveCheckpointContext = {
    state,
    declaredMove,
    choices: undefined,
    interactive: false,
    // No consumer draws during compile; an empty script throws if one ever starts to.
    dice: scriptedDiceSource([]),
    ...(overlay !== undefined ? { overlay } : {}),
  };

  const entries: MoveForecastEntry[] = fold.kind === "boost" ? [{ kind: "boost", free: fold.free }] : [];
  const trampled = trampledBy(state, { moverId: move.moverId, path });
  if (trampled.length > 0) entries.push({ kind: "trample", unitIds: trampled, unitNames: trampled.map((id) => nameOf(state, id)) });
  for (const offer of compileMoveCheckpointOffers(ctx)) {
    const consumerId = moveCheckpointConsumerId(offer.promptId(ctx)) ?? "";
    entries.push({
      kind: "reaction",
      consumerId,
      unitId: offer.watcherId,
      unitName: state.combatants[offer.watcherId]?.name ?? offer.watcherId,
      reactionName: reactionNameOf({ state, moverId: move.moverId, offer, consumerId, overlay: overlay ?? SEED_OVERLAY }),
    });
  }

  const stoppedBy = stopEngagers(state, { moverId: move.moverId, path });
  for (const id of stoppedBy) entries.push({ kind: "stops", unitId: id, unitName: nameOf(state, id) });

  const moved: EncounterState = {
    ...state,
    combatants: { ...state.combatants, [move.moverId]: { ...mover, pos: move.to } },
  };
  const grapple = grappleConsequence(moved, { moverId: move.moverId, from: mover.pos });
  if (grapple !== undefined) entries.push(grapple);
  for (const id of Object.keys(state.combatants).sort()) {
    if (id === move.moverId || id === grapple?.unitId || stoppedBy.includes(id) || !isEngagedWith(moved, move.moverId, id)) continue;
    entries.push({ kind: "engaged", unitId: id, unitName: nameOf(state, id) });
  }

  const reachers = couldReachNextTurn(state, move.to, move.moverId);
  if (reachers.length > 0) entries.push({ kind: "reachable_by", unitIds: reachers, unitNames: reachers.map((id) => nameOf(state, id)) });

  return { entries, moveLeftAfter: moveLeftAfter(state, move) };
}

/** The move a forecast reads: a destination, and the route a Trampler's drag chose (absent: the engine's route). */
export type ForecastedMove = { moverId: string; to: Cell; path?: readonly Cell[] };

/** `path` when the real `move_token` reducer accepts it to `to`, else `null`: every path rule and the move left live there. */
function chosenPath(state: EncounterState, move: { moverId: string; to: Cell; path: readonly Cell[] }): Cell[] | null {
  const r = applyEncounter(state, { type: "move_token", actorId: move.moverId, combatantId: move.moverId, to: move.to, path: [...move.path] });
  return r.ok ? [...move.path] : null;
}

/** Who a Trampling mover's `path` would deal the Trample damage to: everyone it crosses, unless this turn's one sweep is spent (Q4). */
function trampledBy(state: EncounterState, move: { moverId: string; path: readonly Cell[] }): string[] {
  const mover = state.combatants[move.moverId]!;
  if (!hasStatus(mover.statuses, TRAMPLING_STATUS) || hasSweptTrampleThisTurn(mover.current.resources)) return [];
  return charactersCrossed(state, move.moverId, move.path);
}

/**
 * What the move does to the mover's grapple, read off the room's own post-move chain: the drag
 * recomputer, then adjacency. A dominant mover `drags` its partner; any move that leaves the pair
 * non-adjacent `ends_grapple` (an uncontested same-Size pair walking apart is RAW-legal and ends it,
 * `projects/grapple-move-feel.md` Defect 5). `moved` already has the mover standing at the destination.
 */
function grappleConsequence(moved: EncounterState, args: { moverId: string; from: Cell }): Extract<MoveForecastEntry, { kind: "drags" | "ends_grapple" }> | undefined {
  const partnerId = grapplePartner(moved, args.moverId);
  if (partnerId === undefined || moved.combatants[partnerId]?.pos == null) return undefined;
  const drag = dragRecomputer.recompute({ state: moved, moved: args.moverId, from: args.from });
  const after = applyResolved(moved, drag);
  const unit = { unitId: partnerId, unitName: nameOf(moved, partnerId) };
  if (combatantRange(after, args.moverId, partnerId) !== 1) return { kind: "ends_grapple", ...unit };
  return drag.length > 0 ? { kind: "drags", ...unit } : undefined;
}

/** A drop the room refuses: its forecast is the one `boost_refused` entry, with no moves-left line. */
export const isRefusedForecast = (f: MoveForecast): boolean => f.entries.some((e) => e.kind === "boost_refused");

const nameOf = (state: EncounterState, id: string): string => state.combatants[id]?.name ?? id;

/**
 * Who stops the move on `path`'s last step: the reducer's own test (`movementContext`'s `stopsAt`,
 * which is absent for a Disengaged or Trampling mover) and, when it stops, the newly-Engaging
 * hostiles `newEngagersOnStep` names. The reducer only checks the last step because nothing past a
 * stopping step is reachable by way of it.
 */
function stopEngagers(state: EncounterState, args: { moverId: string; path: Cell[] }): string[] {
  const { moverId, path } = args;
  if (path.length < 2) return [];
  const mover = state.combatants[moverId]!;
  const step = { from: path[path.length - 2]!, to: path[path.length - 1]! };
  const ctx = movementContext(worldFor(state), state, { exceptId: moverId, bulldozeFollows: true });
  return ctx.stopsAt?.(step.from, step.to) ? newEngagersOnStep(state, mover, step) : [];
}

/** The mover's move budget after the drop, read off the real `move_token` reducer so a stop's zero and every cost rule come from the one place they live. */
function moveLeftAfter(state: EncounterState, move: ForecastedMove): number {
  const r = applyEncounter(state, { type: "move_token", actorId: move.moverId, combatantId: move.moverId, to: move.to, ...(move.path !== undefined ? { path: [...move.path] } : {}) });
  if (!r.ok) return 0;
  const turn = r.value.state.activation.turn;
  return turn.phase === "acting" && turn.actor === move.moverId ? turn.budget.move : 0;
}

/**
 * The reaction's display name. An offer carries no name, so an authored one is recovered from the
 * handler index its id ends in (`authored_move_reaction:<moverId>#<idx>`, over the same
 * `gatherStatusHandlers` walk the compiler indexes) and named the way the compiler names the
 * reaction cap it checks (`h.name ?? h.source`).
 */
function reactionNameOf(args: {
  state: EncounterState;
  moverId: string;
  offer: MoveCheckpointOffer;
  consumerId: string;
  overlay: LoadedOverlay;
}): string {
  const { state, moverId, offer, consumerId, overlay } = args;
  if (consumerId === OVERWATCH_CHECKPOINT_ID) return "Overwatch";
  if (consumerId === AUTHORED_MOVE_REACTION_ID) {
    const idx = Number(offer.id.slice(offer.id.lastIndexOf("#") + 1));
    const h = gatherStatusHandlers(state, moverId, overlay)[idx];
    if (h !== undefined) return h.name ?? h.source;
  }
  return consumerId;
}

/** Why the `boost` reducer refuses, in plain words (`boostBlocker`'s kinds). */
const BOOST_REFUSAL_REASON: Record<BoostBlocker["kind"], string> = {
  no_turn: "not this unit's turn",
  not_actor: "not this unit's turn",
  mid_attack: "finish the attack first",
  no_action: "no action left",
  held: "holding a prepared action",
  stunned: "Stunned, no actions",
  braced: "Braced last turn, no normal move",
  slowed: "Slowed, Prone or Grappled",
  immobilized: "Immobilized",
  used: "already Boosted this turn",
};

/**
 * The move advisory's words, shared by the browser callout and `lvtt encounter forecast-move` so the
 * two say the same thing. Plain words, names only: no weapon, threat or range numbers.
 */
export const MOVE_FORECAST_COPY = {
  movesLeft: (n: number): string => `${n} ${n === 1 ? "move" : "moves"} left`,
  boost: (free: boolean): string => (free ? "Boost: free action" : "Boost: costs 1 action"),
  boostRefused: (blocker: BoostBlocker["kind"]): string => `Can't Boost: ${BOOST_REFUSAL_REASON[blocker]}`,
  trample: (unitNames: readonly string[]): string => `TRAMPLE: ${unitNames.join(", ")}`,
  stops: (unitName: string): string => `Stops here: Engaged by ${unitName}`,
  drags: (unitName: string): string => `Drags ${unitName} along`,
  endsGrapple: (unitName: string): string => `Ends the grapple with ${unitName}`,
  engaged: (unitName: string): string => `Engaged — ${unitName}`,
  reaction: (reactionName: string, unitName: string): string => `${reactionName} — ${unitName}`,
  reachableBy: (unitNames: readonly string[]): string => `${unitNames.join(", ")} could reach you here next turn`,
};

/** One entry's line in {@link MOVE_FORECAST_COPY}'s words. */
export function moveForecastText(e: MoveForecastEntry): string {
  switch (e.kind) {
    case "boost":
      return MOVE_FORECAST_COPY.boost(e.free);
    case "boost_refused":
      return MOVE_FORECAST_COPY.boostRefused(e.blocker);
    case "trample":
      return MOVE_FORECAST_COPY.trample(e.unitNames);
    case "reaction":
      return MOVE_FORECAST_COPY.reaction(e.reactionName, e.unitName);
    case "stops":
      return MOVE_FORECAST_COPY.stops(e.unitName);
    case "drags":
      return MOVE_FORECAST_COPY.drags(e.unitName);
    case "ends_grapple":
      return MOVE_FORECAST_COPY.endsGrapple(e.unitName);
    case "engaged":
      return MOVE_FORECAST_COPY.engaged(e.unitName);
    case "reachable_by":
      return MOVE_FORECAST_COPY.reachableBy(e.unitNames);
  }
}
